import { useState } from 'react';
import styled from '@emotion/styled';
import { useMediaQuery } from 'react-responsive';
import { formatDate } from 'views/plugins/booking_request/dates';
import { Pricing } from './pricing';
import {
  GuideLogo,
  Header,
  CancellationPolicy,
  Picture,
  BookingInfo,
  CollapsibleHeader,
} from './components';
import type { SummaryProps } from './types';

export interface CancellationPolicyType {
  description: string;
}

export interface RootUnit {
  noun_singular: string;
  noun_plural: string;
}
export interface Request {
  is_invoice: boolean;
  for_custom_booking: boolean;
  guide_avatar: string;
  tour_picture: string;
  tour_name_translation: string;
  guide_full_name: string;
  has_resource: boolean;
  resource_name: string;
  tour_cancellation_policy: CancellationPolicyType;
  has_cancellation: boolean;
  paid_in_cash: boolean;
  is_live_booking: boolean;
  booking_id: string;
  time: string;
  date: string;
  duration: string;
  number_of_guests: number;
  committed: boolean;
  origin_url: string;
  root_unit: RootUnit;
  iso_time: string;
  online: boolean;
  tour_timezone: string;
  for_zero_priced_tour: boolean;
}

export interface LocationType {
  label: string;
}

export interface PricingData {
  bookingFeeFormatted: string;
  discountFormatted: string;
  discountCode: string;
  onlineDiscountFormatted: string;
  depositFormatted: string;
  unitPriceFormatted: string;
  guestsPriceFormatted: string;
  guests: any;
  totalFormatted: string;
  additionalTax: number;
  additionalTaxFormatted: string;
  additionalTaxLabel: string;
  remainderFormatted: string;
  remainderPaymentDate: string;
  remainderInCash: boolean;
  remainderInCredit: boolean;
  remainderChargeDaysOffset: number;
  remainderInLocalCurrencyFormatted: string;
  totalInLocalCurrency: string;
  totalInLocalCurrencyFormatted: string;
  totalInLocalCurrencyExactFormatted: string;
  paidInCash: boolean;
  addons: Record<string, any>;
  units: Record<string, any>;
  loading: boolean;
}

const SummaryContent = styled.div<{ isExpanded: boolean }>`
  @media (max-width: 767px) {
    display: ${(props) => (props.isExpanded ? 'block' : 'none')};
  }
`;

export const Summary = ({
  request,
  tour_id,
  location,
  pricingData,
}: SummaryProps) => {
  const isMobileView = useMediaQuery({ maxWidth: 767 });
  const [isExpanded, setIsExpanded] = useState(false);
  const [isPriceBreakdownExpanded, setIsPriceBreakdownExpanded] =
    useState(false);
  const isCustomTour = request.is_invoice || request.for_custom_booking;

  const requestInfoProps = {
    locationLabel: location.label,
    date: formatDate(request.date, true),
    time: request.time,
    duration: request.duration,
    numberOfGuests: request.number_of_guests,
    committed: request.committed,
    rootUnit: {
      noun_singular: request.root_unit.noun_singular,
      noun_plural: request.root_unit.noun_plural,
    },
    isoTime: request.iso_time === 'true',
    online: request.online,
    tourTimezone: request.tour_timezone,
  };

  const handlePriceBreakdownToggle = () => {
    setIsPriceBreakdownExpanded(!isPriceBreakdownExpanded);
  };

  const pricingProps = {
    expandedPriceBreakdown: isMobileView ? true : isPriceBreakdownExpanded,
    displaySubtotalIcon: !isMobileView,
    onTogglePriceBreakdown: !isMobileView
      ? handlePriceBreakdownToggle
      : undefined,
    date: request.date,
    guideFullName: request.guide_full_name,
    forZeroPricedTour: request.for_zero_priced_tour,
    ...pricingData,
  };

  const shouldRenderCancellationPolicy =
    !(
      request.paid_in_cash ||
      request.for_custom_booking ||
      request.is_live_booking
    ) && request.tour_cancellation_policy?.description;

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <>
      <CollapsibleHeader
        tourNameTranslation={request.tour_name_translation}
        date={formatDate(request.date, true)}
        time={request.time}
        numberOfGuests={request.number_of_guests}
        rootUnit={{
          noun_singular: request.root_unit.noun_singular,
          noun_plural: request.root_unit.noun_plural,
        }}
        totalFormatted={pricingData.totalFormatted}
        isExpanded={isExpanded}
        onToggle={handleToggle}
        tourPicture={isCustomTour ? request.guide_avatar : request.tour_picture}
      />
      <SummaryContent isExpanded={isExpanded}>
        <div className="CheckoutSummary-Container">
          {!isMobileView && request?.for_custom_booking && (
            <GuideLogo
              picture={request.guide_avatar}
              tourName={request.tour_name_translation}
              guideName={request.guide_full_name}
              key="guide-logo"
            />
          )}
          {!isMobileView && (
            <Picture
              tourPicture={
                isCustomTour ? request.guide_avatar : request.tour_picture
              }
              rounded={isCustomTour}
            />
          )}
          <div className="CheckoutSummary">
            {!isMobileView && (
              <Header
                tourNameTranslation={request.tour_name_translation}
                guideFullName={request.guide_full_name}
                tourId={tour_id}
                isInvoice={request.is_invoice}
                hasResource={request.has_resource}
                resourceName={request.resource_name}
              />
            )}
            {!isMobileView && <BookingInfo {...requestInfoProps} />}
            {shouldRenderCancellationPolicy && (
              <CancellationPolicy
                description={request.tour_cancellation_policy.description}
                hasCancellation={request.has_cancellation}
              />
            )}
            <Pricing {...pricingProps} />
          </div>
        </div>
      </SummaryContent>
    </>
  );
};
